/**
 * Contextual "How we use AI" copy per page type and title.
 * Used by PageAISection on hub, detail, article, case study, and legacy routes.
 */

const AI_SERVICE_PATH = "/services/ai-and-automation";
const AI_RESOURCES_PATH = "/resources/ai-automation-guides";

function titleLower(title = "") {
  return String(title).toLowerCase();
}

function isAiNative(title, group) {
  const t = titleLower(`${title} ${group || ""}`);
  return /ai\b|llm|gpt|langchain|machine learning|mlops|chatbot|openai|claude|gemini|whisper|rag|vector|pytorch|tensorflow|nlp|ocr|automation agent|speech-to-text|text-to-speech/.test(
    t
  );
}

const DEFAULT_CAPS = [
  "Copilot-style assistants for your operators (search, forms, approvals)",
  "Document OCR and classification on invoices, job cards, and reports",
  "Rules + ML alerts on dashboards you already use",
  "Secure integration with OpenAI, Azure OpenAI, or on-prem models",
];

export function getPageAiContent({ title = "", variant = "default", group, slug, hubKey }) {
  const t = title;
  const tl = titleLower(t);

  if (variant === "home") {
    return {
      headline: "AI-enabled software delivery",
      lead: "Jstechno builds ERP, mobile, web, and portal products—and adds practical AI where it saves time: copilots, automation, OCR, and analytics grounded in your data.",
      capabilities: [
        "LLM assistants on documents, job cards, and CRM records",
        "Voice and WhatsApp workflows for operations teams",
        "Forecasting and alerts on live ERP data",
        "Responsible rollout with guardrails and support",
      ],
      ctaPrimary: "Explore AI services",
      ctaPrimaryPath: AI_SERVICE_PATH,
      ctaSecondary: "Discuss a project",
      ctaSecondaryPath: "/contact",
    };
  }

  if (variant === "hub") {
    const hubMap = {
      services: {
        headline: "AI across our services",
        lead: "Every service line can include practical AI—not generic chatbots. We scope assistants, automation, and analytics to your workflows and compliance needs.",
        capabilities: [
          "Discovery workshops to find high-ROI automation in ERP, CRM, and portals",
          "LLM assistants grounded on your documents and database (RAG)",
          "Voice and WhatsApp bots tied to real business data",
          "MLOps-friendly delivery with logging, guardrails, and human review",
        ],
      },
      solutions: {
        headline: "AI inside our solutions",
        lead: "ERP, CRM, job-card, LIMS, and portal solutions can ship with optional AI modules—forecasting, recommendations, and operator copilots.",
        capabilities: [
          "Demand and inventory signals on ERP data",
          "Lead scoring and next-best-action in CRM",
          "Smart routing and SLA predictions on service desks",
          "Configurable AI features per module—not one-size-fits-all",
        ],
      },
      technologies: {
        headline: "AI + modern stacks",
        lead: "We build production AI on the same technologies listed here—React frontends, Laravel/Node APIs, vector stores, and cloud GPU when needed.",
        capabilities: [
          "LangChain / custom agents on your APIs",
          "Fine-tuned models where cloud LLMs are not enough",
          "Real-time inference with Redis, Kafka, and Kubernetes",
          "Evaluation harnesses before go-live",
        ],
      },
      industries: {
        headline: "AI for your industry",
        lead: "Manufacturing, labs, aerospace, healthcare, and retail programmes each get AI scoped to real constraints—not pilot demos that never reach production.",
        capabilities: [
          "Job-card and instrument data summarisation for R&D teams",
          "Quality anomaly detection on production metrics",
          "Customer support copilots with audit trails",
          "Executive briefings generated from live ERP/CRM data",
        ],
      },
      caseStudies: {
        headline: "AI in how we deliver",
        lead: "Case studies reflect full engineering delivery; many engagements add AI for reporting, search, or workflow automation where it clearly pays off.",
        capabilities: [
          "Accelerated test design and regression analysis",
          "Natural-language search across reports and attachments",
          "Automated status updates across centres and teams",
          "Post-go-live tuning of prompts and business rules",
        ],
      },
      resources: {
        headline: "AI guides & delivery notes",
        lead: "Our resources include AI automation playbooks—how to adopt LLMs, voice agents, and workflow bots without risking data leakage.",
        capabilities: [
          "RAG architecture patterns for business documents",
          "Comparing cloud LLMs vs on-prem open models",
          "Change management for operations teams",
          "Security checklist for AI features",
        ],
      },
      careers: {
        headline: "AI at Jstechno engineering",
        lead: "Engineers work with modern AI tooling for specifications, code review, and test generation—always with human sign-off on production changes.",
        capabilities: [
          "Pairing on real client AI features, not toy demos",
          "Mentorship on LLM APIs, vector DBs, and MLOps basics",
          "Responsible AI guidelines in code review",
          "Exposure to ERP, mobile, and automation programmes",
        ],
      },
    };
    const block = hubMap[hubKey] || hubMap.services;
    return { ...block, ctaPrimary: "AI & automation services", ctaPrimaryPath: AI_SERVICE_PATH, ctaSecondary: "AI guides", ctaSecondaryPath: AI_RESOURCES_PATH };
  }

  if (variant === "case-study") {
    return {
      headline: `AI opportunities on ${t}`,
      lead: `For engagements like ${t}, Jstechno often layers AI where data already exists in the system—summaries for multi-centre teams, faster search, and assisted reporting after go-live.`,
      capabilities: [
        "Natural-language search across job cards, tests, and reports",
        "Auto-summaries for SAC / remote centre handoffs",
        "Anomaly flags on instrument or production metrics",
        "Secure assistants trained only on client-approved data",
      ],
      ctaPrimary: "Discuss AI for your programme",
      ctaPrimaryPath: "/contact",
      ctaSecondary: "AI automation guides",
      ctaSecondaryPath: AI_RESOURCES_PATH,
    };
  }

  if (variant === "article" || variant === "resource") {
    return {
      headline: `AI perspective: ${t}`,
      lead: `This topic connects to how Jstechno delivers software today—with optional AI modules when they reduce manual work or improve decisions, not as a replacement for solid engineering.`,
      capabilities: [
        "Map article concepts to ERP, CRM, or custom platform features",
        "Propose phased AI rollout (assist → automate → predict)",
        "Align with India/global compliance and hosting choices",
        "Measure ROI with clear before/after metrics",
      ],
      ctaPrimary: "Talk to our team",
      ctaPrimaryPath: "/contact",
      ctaSecondary: "AI resource library",
      ctaSecondaryPath: AI_RESOURCES_PATH,
    };
  }

  if (variant === "career") {
    return {
      headline: "AI in your day-to-day engineering",
      lead: `In the ${t} role you will use AI-assisted tooling for specs, tests, and code review—while shipping features that may include LLM, voice, or automation components for clients.`,
      capabilities: [
        "Cursor/IDE assistants under team standards",
        "Building RAG and API integrations on client projects",
        "Writing evaluation tests for prompts and agents",
        "Documenting AI features for support teams",
      ],
      ctaPrimary: "Apply now",
      ctaPrimaryPath: "/contact",
      ctaSecondary: "Life at Jstechno",
      ctaSecondaryPath: "/who-we-are/life-at-jstechno",
    };
  }

  if (variant === "who-we-are") {
    return {
      headline: "Our approach to AI",
      lead: `On ${t}, Jstechno’s view is simple: AI should sit inside the workflows you already run—ERP, portals, labs, and job-card systems—with guardrails and measurable outcomes.`,
      capabilities: [
        "Pragmatic pilots tied to business KPIs",
        "No mandatory cloud LLM—on-prem options when required",
        "Cross-functional discovery with operations teams",
        "Long-term support after AI features go live",
      ],
      ctaPrimary: "AI services",
      ctaPrimaryPath: AI_SERVICE_PATH,
      ctaSecondary: "Contact leadership",
      ctaSecondaryPath: "/contact",
    };
  }

  if (variant === "process") {
    return {
      headline: "AI across our delivery process",
      lead: "From discovery through support, we use AI to speed up analysis, testing, and documentation—while architects and QA retain sign-off on production releases.",
      capabilities: [
        "Workshop note summarisation and user-story drafts",
        "Test case suggestions from requirements and APIs",
        "Log triage and regression clustering in support",
        "Runbook assistants for operators after go-live",
      ],
      ctaPrimary: "Start a discovery call",
      ctaPrimaryPath: "/contact",
      ctaSecondary: "AI & automation",
      ctaSecondaryPath: AI_SERVICE_PATH,
    };
  }

  if (variant === "contact") {
    return {
      headline: "Tell us where AI should help first",
      lead: "On your discovery call we will identify 2–3 concrete AI use cases—assistants, OCR, forecasting, or workflow bots—mapped to systems you already use.",
      capabilities: [
        "No obligation AI opportunity assessment",
        "Rough effort and phased roadmap",
        "Data privacy and hosting options explained plainly",
        "Examples from ERP, mobile, and multi-centre programmes",
      ],
      ctaPrimary: "AI & automation services",
      ctaPrimaryPath: AI_SERVICE_PATH,
      ctaSecondary: "Read AI guides",
      ctaSecondaryPath: AI_RESOURCES_PATH,
    };
  }

  if (variant === "technology" || group) {
    if (isAiNative(t, group)) {
      return {
        headline: `Building with ${t}`,
        lead: `Jstechno delivers production features with ${t}—including inference APIs, agent orchestration, evaluation, and monitoring—not experimental notebooks only.`,
        capabilities: [
          "Solution architecture for latency, cost, and accuracy",
          "Integration with Laravel, Node, or mobile clients",
          "Vector stores, caching, and fallback strategies",
          "Compliance-friendly logging and redaction",
        ],
        ctaPrimary: "Discuss an AI project",
        ctaPrimaryPath: "/contact",
        ctaSecondary: "View AI stack guides",
        ctaSecondaryPath: AI_RESOURCES_PATH,
      };
    }
    return {
      headline: `AI + ${t}`,
      lead: `We use ${t} as the foundation for reliable apps, and add AI where it helps—copilots, automation, or analytics—without compromising maintainability.`,
      capabilities: [
        `${t} APIs and UIs for AI-assisted workflows`,
        "Background jobs for model calls and embeddings",
        "Feature flags to roll out AI modules safely",
        "Observability on prompts, tokens, and errors",
      ],
      ctaPrimary: "AI & automation",
      ctaPrimaryPath: AI_SERVICE_PATH,
      ctaSecondary: `Explore ${t}`,
      ctaSecondaryPath: slug ? `/technologies/${slug}` : "/technologies",
    };
  }

  if (variant === "industry") {
    return {
      headline: `AI for ${t}`,
      lead: `In ${t}, Jstechno implements AI on top of operational software—job cards, ERP, LIMS, CRM—so teams gain speed without replacing systems they trust.`,
      capabilities: [
        "Industry-specific document and form understanding",
        "Cross-location status summaries and alerts",
        "Demand, quality, or compliance forecasting where data allows",
        "Operator copilots with role-based access",
      ],
      ctaPrimary: "Plan AI for your industry",
      ctaPrimaryPath: "/contact",
      ctaSecondary: "AI guides",
      ctaSecondaryPath: AI_RESOURCES_PATH,
    };
  }

  if (variant === "solution") {
    return {
      headline: `AI modules for ${t}`,
      lead: `${t} can include optional AI capabilities—smart defaults, recommendations, voice/WhatsApp interfaces, and automated approvals—scoped per module during discovery.`,
      capabilities: [
        "Embedded assistants on transactional screens",
        "Predictive fields on orders, inventory, or leads",
        "Integration with WhatsApp, email, and voice channels",
        "Audit-friendly logs of AI suggestions vs human decisions",
      ],
      ctaPrimary: "Request a solution demo",
      ctaPrimaryPath: "/contact",
      ctaSecondary: "AI services",
      ctaSecondaryPath: AI_SERVICE_PATH,
    };
  }

  if (variant === "service" || variant === "sub-service") {
    if (isAiNative(t)) {
      return {
        headline: `${t} at Jstechno`,
        lead: `We deliver ${t} end-to-end—use-case discovery, model/API selection, integration, evaluation, and support—aligned with your security and hosting policies.`,
        capabilities: [
          "Production agents and copilots, not one-off demos",
          "Grounded answers on your documents and databases",
          "Voice, OCR, and workflow automation options",
          "Handover runbooks for your operations team",
        ],
        ctaPrimary: "Book a consultation",
        ctaPrimaryPath: "/contact",
        ctaSecondary: "All services",
        ctaSecondaryPath: "/services",
      };
    }
    return {
      headline: `AI with ${t}`,
      lead: `${t} engagements often add AI where it saves time—requirements analysis, test assistance, support bots, or domain-specific copilots on the software we build for you.`,
      capabilities: DEFAULT_CAPS,
      ctaPrimary: "Add AI to this service",
      ctaPrimaryPath: "/contact",
      ctaSecondary: "AI & automation",
      ctaSecondaryPath: AI_SERVICE_PATH,
    };
  }

  if (variant === "legacy") {
    return {
      headline: `AI and ${t}`,
      lead: `Jstechno can modernise ${t} programmes with maintainable engineering and practical AI—automation, analytics, and assistants that respect your operations team.`,
      capabilities: DEFAULT_CAPS,
      ctaPrimary: "Discuss your project",
      ctaPrimaryPath: "/contact",
      ctaSecondary: "Our services",
      ctaSecondaryPath: "/services",
    };
  }

  return {
    headline: `How we apply AI to ${t}`,
    lead: "Jstechno combines solid software delivery with optional AI features—scoped, measurable, and supportable after launch.",
    capabilities: DEFAULT_CAPS,
    ctaPrimary: "AI & automation",
    ctaPrimaryPath: AI_SERVICE_PATH,
    ctaSecondary: "Contact us",
    ctaSecondaryPath: "/contact",
  };
}

/** Routes that already render PageAISection inside v2 templates */
const V2_HAS_INLINE_AI = [
  /^\/services(\/|$)/,
  /^\/solutions(\/|$)/,
  /^\/technologies(\/|$)/,
  /^\/industries(\/|$)/,
  /^\/case-studies(\/|$)/,
  /^\/resources(\/|$)/,
  /^\/who-we-are\/[^/]+$/,
  /^\/careers\/[^/]+$/,
  /^\/blog\/[^/]+$/,
  /^\/process-we-follow$/,
  /^\/technologies$/,
];

export function shouldUseShellPageAi(pathname = "") {
  if (!pathname || pathname === "/") return false;
  if (V2_HAS_INLINE_AI.some((re) => re.test(pathname))) return false;
  return true;
}

export function resolveAiContextFromPath(pathname = "") {
  const p = pathname.replace(/\/$/, "") || "/";
  if (p === "/contact" || p === "/Contact") return { title: "Contact", variant: "contact" };
  if (p === "/careers") return { title: "Careers", variant: "hub", hubKey: "careers" };
  if (p === "/who-we-are") return { title: "Who We Are", variant: "hub", hubKey: "whoWeAre" };
  if (p === "/blog") return { title: "Blog", variant: "hub", hubKey: "resources" };
  if (p === "/industries") return { title: "Industries", variant: "hub", hubKey: "industries" };
  const segments = p.split("/").filter(Boolean);
  const last = decodeURIComponent(segments[segments.length - 1] || "").replace(/-/g, " ");
  return { title: last || "Software", variant: "legacy" };
}
